import { useState, useEffect, useCallback } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { Mail, CheckCircle, Loader2 } from 'lucide-react'
import { authClient } from '@papan/auth/client'
import { useTranslation } from '@papan/i18n'
import { useAuth } from '../hooks/useAuth'
import { AuthLayout } from '../components/AuthLayout'
import { safeNext } from '../lib/safe-next'
import { refusalIsSafeToSurface } from '../lib/refusal'

/**
 * How long to disable Resend after a refusal — and the split is NOT cosmetic.
 *
 * `EMAIL_TIER` is capacity 10 refilling ONE TOKEN PER 60s
 * (`policyFromWindow(10, 600)`), on a bucket keyed by address and SHARED with
 * `/sign-in/email`, `/sign-up/email` and `/request-password-reset` — which this
 * branch put `/send-verification-email` into. So any client backoff below 60s
 * lets a user outpace the refill and drain the bucket that their own sign-in
 * and password reset depend on.
 *
 * Two rounds pulled this in opposite directions and neither was right:
 *
 *   - an unconditional 60s contradicted itself during a rolling restart. The
 *     fetch never leaves the browser, the other instance is already serving,
 *     and the page said "Please try again" while blocking the only Retry
 *     control for a minute.
 *   - dropping it for every non-429 removed the bound entirely, and a flat 5s
 *     replacement was a **12x overdraw** — the docblock claimed clicking "cannot
 *     outpace the bucket" and the arithmetic said otherwise. Measured against
 *     the real buckets: the auth tier 429s at ~35s and the email bucket is
 *     empty at ~115s.
 *
 * The requirements cannot both be met by one number, because they are about
 * different things. So the question is not how long, it is WAS A TOKEN SPENT:
 *
 *   - **no status** — `authFetch`'s catch, i.e. the request never reached the
 *     server. Offline, DNS, CORS, an origin down mid-restart. Nothing was
 *     consumed, so nothing needs bounding beyond stopping a click storm.
 *   - **any HTTP status** — it reached the limiter. A 2xx/4xx that was ALLOWED
 *     spent a token; a 429 was refused and spent nothing (the bucket deducts
 *     only when it can pay). Either way the attempt reached the server and the
 *     next one should wait at least the refill interval, so both take 60.
 *
 * The discriminator is imperfect at the edges and that is accepted: a 2xx with
 * an unparseable body (the CF-Pages-shell misconfig) carries a status the API
 * never issued, and a connection dropped after the server processed a request
 * carries none. Both are rare, and both err by at most 55 seconds.
 */
const NO_TOKEN_SPENT_BACKOFF_SEC = 5
const TOKEN_SPENT_BACKOFF_SEC = 60

export function VerifyEmailPage() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const { refreshSession } = useAuth()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const email = searchParams.get('email') || ''
  // Honor `?next=` so recipients who came in via a share-link detour
  // (signed up → verified) return to the share, not the drive root.
  const nextPath = safeNext(searchParams.get('next'))
  // Back to sign-in keeps where the person was headed.
  const loginPath = searchParams.get('next') ? `/login?next=${encodeURIComponent(nextPath)}` : '/login'

  const [status, setStatus] = useState<'idle' | 'verifying' | 'success' | 'error'>('idle')
  const [error, setError] = useState('')
  const [cooldown, setCooldown] = useState(0)
  const [resent, setResent] = useState(false)
  const [resendError, setResendError] = useState('')
  const [resending, setResending] = useState(false)

  // Better Auth verifies the token server-side and redirects here without params.
  // Check session on mount — if already verified, show success and redirect.
  useEffect(() => {
    if (token) return // token path handled below
    if (email) return // waiting path — user hasn't clicked the link yet

    // No token, no email — likely a redirect after server-side verification
    const checkSession = async () => {
      setStatus('verifying')
      const user = await refreshSession()
      if (user?.emailVerified) {
        setStatus('success')
        setTimeout(() => navigate(nextPath), 1500)
      } else {
        setStatus('idle')
      }
    }
    checkSession()
  }, [token, email, refreshSession, navigate, nextPath])

  // Auto-verify when token is present (client-side verification path)
  useEffect(() => {
    if (!token) return
    setStatus('verifying')

    const verify = async () => {
      try {
        const result = await authClient.verifyEmail({ token }) as unknown as { error?: { message?: string } | null }
        if (result.error) {
          setStatus('error')
          setError(result.error.message || t('verifyEmail.failed'))
          return
        }
        setStatus('success')
        await refreshSession()
        setTimeout(() => navigate(nextPath), 1500)
      } catch {
        setStatus('error')
        setError(t('verifyEmail.expired'))
      }
    }

    verify()
  }, [token, navigate, t, refreshSession, nextPath])

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setInterval(() => setCooldown(c => c - 1), 1000)
    return () => clearInterval(timer)
  }, [cooldown])


  const handleResend = useCallback(async () => {
    // `resending` is the in-flight half, matching ForgotPasswordForm's
    // `disabled={isLoading}`. `cooldown` alone did not stop a second request
    // while the first was still open.
    if (cooldown > 0 || resending || !email) return
    setResending(true)
    setResent(false)

    setResendError('')

    try {
      // Preserve ?next= into the resend's callbackURL so the new
      // email link still lands back on the share. Without this, a
      // recipient who resends loses the share-view destination on
      // the next attempt (round-7 cleanup of the round-6 fix).
      const result = await authClient.sendVerificationEmail({
        email,
        // No ?next= gives `/` (safeNext), which the server leaves out of the
        // link, so the shell picks home by the tenant's mode.
        callbackURL: `${window.location.origin}${nextPath}`,
      })
      // The SAME rule the reset form uses — `refusalIsSafeToSurface`, not a
      // hand-written status check.
      //
      // This page shipped with `status === 429` alone, so everything else still
      // reported success: with the API unreachable (offline, DNS, a CORS block,
      // an origin down mid rolling-restart) `authFetch`'s catch returns an error
      // with NO status, and the page rendered "Verification email sent!" plus a
      // 60s cooldown for a request that never left the browser. A 404 — the
      // literal mechanism of #610, should this endpoint ever be renamed too —
      // behaved identically.
      //
      // The enumeration argument is weaker here than on the reset form, not
      // stronger: the anonymous branch of `/send-verification-email` has no
      // post-lookup 4xx at all, and its mail send is swallowed by
      // `runInBackgroundOrAwait`, so not even a 5xx is account-correlated. The
      // shared predicate is therefore stricter than this surface needs, which is
      // the safe direction and keeps one rule instead of two.
      const status = result?.error?.status
      if (result?.error && refusalIsSafeToSurface(status)) {
        setResendError(status === 429 ? t('common:tooManyRequests') : t('common:requestFailed'))
        // The bound is chosen by WHETHER A TOKEN WAS SPENT — see the constants
        // at the top of this file for the arithmetic. This inline comment used
        // to say "cooldown only for the 429", which was the round-8 rule and
        // survived two rewrites of the rule above it; the next reader of
        // `handleResend` would have taken it as governing.
        //
        // One correction to the premise those rounds were built on: a REFUSED
        // request deducts nothing (`token-bucket.ts`: `if (tokens >= cost)`),
        // so a click loop cannot drain past the bucket's own capacity and the
        // server limiter is the real defence. This bound exists so the UI does
        // not invite a user to spend their own remaining tokens, not because it
        // is what protects the bucket.
        setCooldown(status === undefined ? NO_TOKEN_SPENT_BACKOFF_SEC : TOKEN_SPENT_BACKOFF_SEC)
        return
      }
      setResent(true)
      setCooldown(60)
    } catch {
      // Silently fail — don't reveal if email exists
      setResent(true)
      setCooldown(60)
    } finally {
      setResending(false)
    }
  }, [email, cooldown, resending, nextPath, t])

  // Verifying state (both token path and session-check path)
  if (status === 'verifying') {
    return (
      <AuthLayout>
        <div className="text-center py-4">
          <Loader2 size={40} className="mx-auto mb-4 themed-text-muted animate-spin" />
          <p className="themed-text-secondary">{t('verifyEmail.checking')}</p>
        </div>
      </AuthLayout>
    )
  }

  // Success state
  if (status === 'success') {
    return (
      <AuthLayout>
        <div className="text-center py-4">
          <CheckCircle size={40} className="mx-auto mb-4 text-green-400" />
          <h2 className="text-lg font-semibold themed-text mb-2">{t('verifyEmail.success')}</h2>
          <p className="text-sm themed-text-muted">{t('verifyEmail.successRedirecting')}</p>
        </div>
      </AuthLayout>
    )
  }

  // Error state
  if (status === 'error') {
    return (
      <AuthLayout
        footer={
          <Link to={loginPath} className="text-blue-400 hover:text-blue-300">
            {t('verifyEmail.backToLogin')}
          </Link>
        }
      >
        <div className="text-center py-4">
          <p className="text-sm text-red-400 mb-4">{error}</p>
          {/* No resendError render here, and that is not an oversight.
              `status === 'error'` is set ONLY inside the `if (!token) return`
              effect, so reaching it implies a token — and the sole producer of
              a `?token=` URL (shared/auth/src/server.ts:294) never carries
              `email`. The button below is gated on `email`, so neither it nor a
              message beside it can ever render here. A copy was added in round
              4 on the mistaken reading that four `?email=` call sites reach this
              branch; they navigate WITHOUT a token and land on check-email. */}
          {email && (
            <button
              onClick={handleResend}
              disabled={cooldown > 0 || resending}
              className="text-sm text-blue-400 hover:text-blue-300 disabled:opacity-50"
            >
              {cooldown > 0
                ? t('verifyEmail.cooldown', { seconds: cooldown })
                : resending ? t('verifyEmail.sending')
                : t('verifyEmail.resend')}
            </button>
          )}
        </div>
      </AuthLayout>
    )
  }

  // Waiting for verification flow (no token, showing "check your email")
  return (
    <AuthLayout
      footer={
        <Link to={loginPath} className="text-blue-400 hover:text-blue-300">
          {t('verifyEmail.backToLogin')}
        </Link>
      }
    >
      <div className="text-center py-4">
        <Mail size={40} className="mx-auto mb-4 themed-text-muted" />
        <h2 className="text-lg font-semibold themed-text mb-2">
          {t('verifyEmail.checkEmailTitle')}
        </h2>
        <p className="text-sm themed-text-secondary mb-6">
          {t('verifyEmail.sentTo', { email: email || '...' })}
        </p>

        {/* `alert`, not `status`, matching ForgotPasswordForm: a refusal is
            something the user must act on, and since the cooldown became
            429-only a non-429 refusal changes nothing about the focused button
            — so this region is the only feedback there is. */}
        {resendError && (
          <p className="text-sm text-red-400 mb-4" role="alert">{resendError}</p>
        )}
        {resent && (
          <p className="text-sm text-green-400 mb-4">{t('verifyEmail.resent')}</p>
        )}

        <p className="text-sm themed-text-muted mb-2">{t('verifyEmail.didntReceive')}</p>
        <button
          onClick={handleResend}
          disabled={cooldown > 0 || resending || !email}
          className="text-sm text-blue-400 hover:text-blue-300 disabled:opacity-50"
        >
          {cooldown > 0
            ? t('verifyEmail.cooldown', { seconds: cooldown })
            : resending ? t('verifyEmail.sending')
            : t('verifyEmail.resend')}
        </button>
      </div>
    </AuthLayout>
  )
}
